import _ from 'underscore';

// only leave the top 100 string
function capValue(value) {
  const maxLength = 100;
  if (!_.isString(value) || value.length < maxLength) {
    return value;
  }
  return value.substring(0, maxLength);
}

/**
 * Generate description string for filter
 * @param {Object} filter - filter
 * @param {Object[]} filter.FilterExpressions - required
 *   should in format of [{SelectedColumn, SelectedOperator, Values}]
 * @param {Object[]} filter.idBased - optinal, true for the id based filter
 *
 * @returns {String} generated description string
 */
export function toDescription(filter) {
  if (!filter || !filter.FilterExpressions) {
    return null;
  }

  const expressions = filter.FilterExpressions;

  if (!_.isArray(expressions) || expressions.length === 0) {
    return null;
  }

  if (filter.idBased) {
    return JSON.stringify({ idsCount: expressions[0].Values.length });
  }

  const compact = _.map(
    expressions,
    expr => [expr.SelectedColumn, expr.SelectedOperator, _.map(expr.Values, capValue)]
  );

  return JSON.stringify(compact);
}

/**
 * Generate filter from  description string
 * @param {String} description - the description string
 *
 * @returns {Object} generated filter
 */
export function fromDescription(description) {
  if (!description) {
    return null;
  }

  let compactFilter = JSON.parse(description);

  if (compactFilter.idsCount) {
    return {
      idBased: true,
      idsCount: compactFilter.idsCount,
    };
  }

  compactFilter = compactFilter && compactFilter.Filter ? compactFilter.Filter : compactFilter;
  const filter = _.map(
    compactFilter,
    _.bind(_.object, _, ['SelectedColumn', 'SelectedOperator', 'Values'])
  );

  return {
    FilterExpressions: filter,
  };
}

/**
 * Generate rule description string for filter
 * @param {Object} filter - filter
 * @param {Object[]} filter.FilterExpressions - required
 *   should in format of [{SelectedColumn, SelectedOperator, Values}]
 * @param {Object[]} filter.idBased - optinal, true for the id based filter
 * @param {boolean} acrossAllPages - whether selection is across all pages
 * @param {integer} idsCount - count for id, required when filter.idBased is true
 *
 * @returns {String} generated rule description string
 */
export function toRuleDescription(filter, acrossAllPages, idsCount) {
  if (!filter || !filter.FilterExpressions ||
      !_.isArray(filter.FilterExpressions) ||
      filter.FilterExpressions.length === 0) {
    return null;
  }

  const expressions = filter.FilterExpressions;
  const result = {};

  if (filter.idBased) {
    result.idsCount = acrossAllPages ? idsCount : expressions[0].Values.length;
  }

  // in acrossallpages senario, the values for first expression is always empty
  // which is useless and may cause confusion in description, so we remove it
  const expressionsToMap = acrossAllPages ? _.without(expressions, expressions[0]) : expressions;
  const resultFilter = _.map(expressionsToMap, (expr) => {
    const mappedResult = [expr.SelectedColumn, expr.SelectedOperator, _.map(expr.Values, capValue)];

    if (!_.isEmpty(expr.SelectedNames)) {
      mappedResult.push(_.map(expr.SelectedNames, capValue));
    }

    return mappedResult;
  });

  result.Filter = resultFilter;
  return JSON.stringify(result);
}

/**
 * Generate filter from  rule description string
 * @param {String} description - the rule description string
 *
 * @returns {Object} generated filter
 */
export function fromRuleDescription(description) {
  if (!description) {
    return null;
  }

  const compact = JSON.parse(description);
  const compactFilter = compact.Filter ? compact.Filter : compact;
  const FilterExpressions = _.map(compactFilter, (element) => {
    const mapped = {
      SelectedColumn: element[0],
      SelectedOperator: element[1],
      Values: element[2],
    };

    if (element[0] === 'Labels' && element.length > 3) {
      mapped.SelectedNames = element[3]; // eslint-disable-line
    }

    return mapped;
  });

  return compact.idsCount ?
    {
      idBased: true,
      idsCount: compact.idsCount,
      FilterExpressions,
    } :
    {
      FilterExpressions,
    };
}

/**
 * Generate legacy query string from filterExpressions
 * @param {Object[]} filterExpressions - the filter expressions
 *
 * @returns {String} filter expression part
 */
export function toLegacyQueryString(filterExpressions) {
  const expressionStrings = _.map(
    filterExpressions,
    (expression, index) => _.map(expression, (value, key) => {
      const template = `filter[${index}].${key}=`;

      return _.isArray(value) ?
        _.map(value, val => template + val).join('&') :
        template + value;
    }).join('&')
  );

  return expressionStrings.join('&');
}

